/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE REVIEW WRITE — the mode, next week's column, and the rows `Send review` inserts (W82 §2 right column, §6, §7; the
 * pro web step 4). PURE; `reviewWrite.test.ts` runs it. The page (`WorkoutReview.tsx`) holds the DRAFTS and calls this.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 *   • THE MODE — `profiles.review_mode` (migration `20260926100000`): `with_review` (the default: the page carries a
 *     Next-week column and ONE primary sends the review AND writes next week), `separately` (`Send review`; numbers are
 *     set on the client's Week), `fresh_each_week` (`Send review`; the coach writes a new workout each week).
 *   • NEXT WEEK — the copy of the SAME template after the reviewed day (`coalesce(root_workout_id, workout_id)`, the
 *     overlay's and the unique index's identity). A cell is ONE set row of that copy and ONE field — the one the row is
 *     about (a hold's seconds, a loaded row's kg, else the reps; `setVerdict.classify`'s own order). Prefilled with what
 *     the copy holds, which IS the weekly adjustment's proposal (`nextWeekProposal`: the web proposes nothing of its own);
 *     `proposed` = it differs from this week's value, outlined in the accent. The write is `setFieldWrite` — the
 *     overlay's `Hold` path, one function.
 *   • THE WORDS UNDER THE BUTTON — `compareWeeks` → `groupMoves` → `MoveParts`, the Week overlay's own reader and drawing,
 *     with the drafts applied; plus `held (set 4 short)` where next week repeats a week that fell short.
 *   • THE ROWS — ONE insert of the review and every comment (`reviewBody.ts` grammar), as the coach. Nothing is ever
 *     UPDATEd: the grant is `update (read_at)` alone, so a sent review is final (the page says so).
 */

import { OVERALL, mediaFields, type MediaFields, type Slot, type SlotKey } from "./reviewMedia";
import type { AssignedWorkoutTree, TreeExercise, TreeSet } from "./assignedWorkoutTree";
import { flattenWeek } from "./nextWeekProposal";
import { groupMoves, NO_NEXT_WEEK_LINE, type MovedExercise } from "./proposalOverlay";
import { compareWeeks, unitKey } from "./weekDelta";
import type { SetColumn, SetFieldWrite } from "./setFieldWrite";
import { commentBody, reviewBody } from "./reviewBody";
import { setWords } from "./reviewSheet";
import { phoneDose } from "./phoneDose";

/* ── the mode ───────────────────────────────────────────────────────────────────────────────────── */

export type ReviewMode = "with_review" | "separately" | "fresh_each_week";
export const REVIEW_MODES: readonly { readonly value: ReviewMode; readonly label: string; readonly sentence: string }[] = [
  { value: "with_review", label: "With each review", sentence: "The review shows next week's numbers beside what they did, and one button sends the review and sets them." },
  { value: "separately", label: "Separately, on the client's Week", sentence: "The review is words only; you set next week's numbers on the client's Week." },
  { value: "fresh_each_week", label: "I write a fresh workout each week", sentence: "The review is words only; you assign a new workout for each week." },
];
/** An unknown or absent value is the column's own default — never a fourth shape. */
export function reviewModeOf(raw: string | null | undefined): ReviewMode {
  return raw === "separately" || raw === "fresh_each_week" ? raw : "with_review";
}

/** Whether the page draws the Next-week column: `with_review` AND a next-week copy exists. */
export function showsNextWeek(mode: ReviewMode, hasNext: boolean): boolean {
  return mode === "with_review" && hasNext;
}
/** The ONE primary's label. */
export function primaryLabel(mode: ReviewMode, hasNext: boolean): string {
  return showsNextWeek(mode, hasNext) ? "Send review · set next week" : "Send review";
}
/** What the Next-week card says when it has no column: the Week's own sentence, or the mode's. */
export function nextWeekAbsent(mode: ReviewMode): string | null {
  if (mode === "with_review") return NO_NEXT_WEEK_LINE;
  if (mode === "separately") return "You set next week on their Week.";
  return "You assign a new workout for next week.";
}

/* ── N2d: REPEAT NEXT WEEK — the coach's act where no next week exists ─────────────────────────────────
 * QUEUE N2d (Claude's ruling 2026-10-06, no Fable listed): week 2 had ONE writer — the phone's automatic switch, default
 * OFF — so a coach in `with_review` reviewed week 1 and found no Next-week column and nothing to press. The answer is the
 * COACH's act, not the engine's (his 10-06 order: the engine stays hidden): `Repeat next week` clones THIS week's copy
 * (`assign_coach_workout`, copy-on-assign — the root follows, so the copy IS next week's copy of the same template) seven
 * days on, tells the client (`tellTheClient`), and re-reads — the column then appears holding this week's numbers, and
 * `Send review · set next week` sets them. Offered only where it can keep its promise: `with_review`, no later copy of
 * this template, and a dated single training copy (the reads' own rule). */
export const REPEAT_LABEL = "Repeat next week";
/** The day a repeat lands — seven days on (`CoachAutoApplyDue`'s own step), date-only, no clock and no DST. */
export function repeatDay(day: string): string {
  const [y = 0, m = 1, d = 1] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + 7)).toISOString().slice(0, 10);
}
export function canRepeat(mode: ReviewMode, hasNext: boolean, thisWorkoutId: string | null, day: string | null): boolean {
  return mode === "with_review" && !hasNext && thisWorkoutId !== null && day !== null;
}

export const UNDO_SECONDS = 10;
export const SENT_LINE = "Sent · add to it in the thread";
export const MODE_LINK = "How you set next week ›";

/* ── next week's cells ──────────────────────────────────────────────────────────────────────────── */

export type NextField = "weightKg" | "reps" | "holdSeconds";
export type NextCell = {
  readonly setId: string;
  readonly field: NextField;
  /** What next week's copy holds now. */
  readonly value: number | null;
  /** This week's value of the same field at the same place (null = no such set, or no value). */
  readonly was: number | null;
  /** Next week differs from this week — the weekly adjustment moved it (outlined in the accent). */
  readonly proposed: boolean;
  /** W136 B2 S3r2 A2 (RULED, Fable 2026-10-05) — next week's row carries `rounds` or `distance_m`: the coach progression
   *  leaves it whole, so the cell reads `same` and the review writes NOTHING to it (no seconds as a hold, no reps on a
   *  distance-only run); a run is changed in the builder. `ReviewWriteRule.Cell.frozen`'s twin (the corpus pins it). */
  readonly frozen: boolean;
  /** W136 B2 S3r3 A3 — a frozen cell's words: `same` ONLY where next week's set i prints exactly as this week's set i
   *  (`setWords`, §5's one formatter), else next week's own phrase (`6 km in 30:00`); null for a cell that is not frozen.
   *  `ReviewWriteRule.Cell.frozenWords`' twin (the corpus pins it). */
  readonly frozenWords: string | null;
  /** 🔴 WARM-UPS GET A CELL (his question 2026-10-07, QUOTED: "why we can't edit the warm up sets numbers in the workout
   *  review page and are greyed out?"). Next week's set is typed `warm_up`: it pairs with this week's warm-up at the same
   *  place (warm-up i ↔ warm-up i), sits on a ramp row (`cellsForRows`), and is never counted as a set.
   *  `ReviewWriteRule.Cell.warmUp`'s twin (the corpus pins it). */
  readonly warmUp: boolean;
};
/** A frozen row's Next-week cell — `ReviewPhoneWords.frozenCell` (gate `w136-b2-s3r2-a-frozen-row-is-not-written` reads both). */
export const FROZEN_CELL = "same";
/** A row any of whose sets carries `rounds` or `distance_m` — measured in rounds or distance, left as written. */
export function isFrozen(e: TreeExercise): boolean {
  // `!= null`, not `!== null`: a tree read without the column carries `undefined`, which must freeze nothing.
  return (e.coach_workout_sets ?? []).some((s) => s.distance_m != null || s.rounds != null);
}

const num = (v: number | string | null): number | null => {
  if (v === null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};
const byPosition = <T extends { position: number | null }>(xs: readonly T[] | null) => [...(xs ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

/** The field a set is ABOUT — a hold's seconds, a loaded set's kg, else the reps (`setVerdict.classify`'s order). */
export function primaryField(s: Pick<TreeSet, "time_seconds" | "weight_kg">): NextField {
  if (s.time_seconds !== null) return "holdSeconds";
  const w = num(s.weight_kg);
  return w !== null && w > 0 ? "weightKg" : "reps";
}
function fieldOf(s: TreeSet, f: NextField): number | null {
  return f === "holdSeconds" ? s.time_seconds : f === "weightKg" ? num(s.weight_kg) : s.reps;
}
const COLUMN: Record<NextField, SetColumn> = { weightKg: "weight_kg", reps: "reps", holdSeconds: "time_seconds" };

/** A tree's exercises in the copy's order — sections by position, exercises by position — with their section's role. */
export function treeExercises(tree: AssignedWorkoutTree): { exercise: TreeExercise; role: string | null }[] {
  const out: { exercise: TreeExercise; role: string | null }[] = [];
  for (const s of byPosition(tree.coach_workout_sections)) for (const e of byPosition(s.coach_workout_exercises)) out.push({ exercise: e, role: s.section_type });
  return out;
}
const isWarmUp = (s: TreeSet) => s.set_type === "warm_up";

/**
 * For every exercise of THIS week's copy, keyed by its row id (`SheetExercise.planRowId`), next week's cells — one per set in next
 * week's order, paired by (library exercise, section role) in order (`weekDelta.unitKey`, the overlay's key; a second row of
 * the same exercise pairs with the second); within it working set i with working set i, warm-up i with warm-up i
 * (`NextCell.warmUp`). Null where next week has no counterpart (the exercise was dropped).
 */
export function nextWeekCells(thisTree: AssignedWorkoutTree, nextTree: AssignedWorkoutTree, kmKeys: ReadonlySet<string>): Map<string, NextCell[] | null> {
  const nextByKey = new Map<string, TreeExercise[]>();
  for (const { exercise, role } of treeExercises(nextTree)) {
    const k = unitKey({ exerciseId: exercise.exercise_id ?? exercise.id, role });
    nextByKey.set(k, [...(nextByKey.get(k) ?? []), exercise]);
  }
  const taken = new Map<string, number>();
  return new Map(treeExercises(thisTree).map(({ exercise, role }): [string, NextCell[] | null] => {
    const k = unitKey({ exerciseId: exercise.exercise_id ?? exercise.id, role });
    const n = taken.get(k) ?? 0;
    taken.set(k, n + 1);
    const next = nextByKey.get(k)?.[n];
    if (next === undefined) return [exercise.id, null];
    const mine = byPosition(exercise.coach_workout_sets);
    const frozen = isFrozen(next);
    const km = next.exercise_id !== null && kmKeys.has(next.exercise_id);
    const seen = { warm: 0, work: 0 };
    return [exercise.id, byPosition(next.coach_workout_sets).map((s) => {
      const warmUp = isWarmUp(s);
      const i = warmUp ? seen.warm++ : seen.work++;
      const field = primaryField(s);
      const value = fieldOf(s, field);
      const w = mine.filter((x) => isWarmUp(x) === warmUp)[i];
      const was = w === undefined ? null : fieldOf(w, field);
      let frozenWords: string | null = null;
      if (frozen) {
        const words = frozenSetWords(s, km);
        frozenWords = w !== undefined && frozenSetWords(w, km) === words ? FROZEN_CELL : words;
      }
      return { setId: s.id, field, value, was, proposed: value !== was, frozen, frozenWords, warmUp };
    })];
  }));
}

/** Each table row's cell, in the table's order: a ramp row the next WARM-UP cell, every other row the next working cell
 *  (`ReviewWriteRule.cellsForRows`' twin — the corpus's `rowCells`). `next` null or absent: no row has one. */
export function cellsForRows(rows: readonly { state: string }[], next: readonly NextCell[] | null | undefined): (NextCell | undefined)[] {
  if (next == null) return rows.map(() => undefined);
  const warm = next.filter((c) => c.warmUp), work = next.filter((c) => !c.warmUp);
  const seen = { warm: 0, work: 0 };
  return rows.map((r) => (r.state === "ramp" ? warm[seen.warm++] : work[seen.work++]));
}

/** One set of a frozen row in §5's words — the Written column's own formatter; its time is TYPED (`30:00`), never a hold. */
export function frozenSetWords(s: TreeSet, km: boolean): string {
  return setWords(s.reps, s.time_seconds, num(s.weight_kg), num(s.distance_m ?? null), s.rounds, km, true);
}

/** A cell's value as its input shows it: `62.5`, `8`, `30`; empty for none. */
export function cellText(v: number | null): string {
  return v === null ? "" : Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100);
}
export function cellUnit(f: NextField): string {
  return f === "weightKg" ? "kg" : f === "holdSeconds" ? "s" : "reps";
}

/** W95 — the unit a Next-week cell SPEAKS: the written cell's, for that row. The written cell says a hold through
 *  `durationLabel`, which says whole minutes as `6 min` — so a hold whose written value (this week's; next week's when
 *  there is none) is whole minutes is typed and read in minutes, never `360 s` beside `6 min`. A hold under a minute, or
 *  with seconds over (`1:30`), stays in seconds, as does a next value minutes cannot say exactly in two decimals. */
export type CellUnit = "kg" | "reps" | "s" | "min";
export function cellUnitFor(cell: Pick<NextCell, "field" | "was" | "value">): CellUnit {
  if (cell.field !== "holdSeconds") return cell.field === "weightKg" ? "kg" : "reps";
  const basis = cell.was ?? cell.value;
  const minutes = basis !== null && basis >= 60 && basis % 60 === 0 && (cell.value === null || cell.value % 3 === 0);
  return minutes ? "min" : "s";
}
/** A cell's value as its input shows it, in `cellUnitFor`'s unit: `6` for a 360-second hold said in minutes. */
export function cellTextFor(cell: Pick<NextCell, "field" | "was" | "value">): string {
  return cellUnitFor(cell) === "min" && cell.value !== null ? cellText(cell.value / 60) : cellText(cell.value);
}

/** What the coach typed, read: a number ≥ 0 (reps and seconds whole), or `invalid`. In minutes (`unit` "min", a hold
 *  `cellUnitFor` says in minutes) up to two decimals that make whole seconds: `6.5` → 390, `6.17` refused. */
export function parseCell(field: NextField, text: string, unit?: CellUnit): number | "invalid" {
  const t = text.trim().replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(t)) return "invalid";
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return "invalid";
  if (field === "holdSeconds" && unit === "min") {
    const centi = Math.round(n * 100);
    if (Math.abs(centi - n * 100) > 1e-6 || centi % 5 !== 0) return "invalid";
    return (centi * 3) / 5;
  }
  if (field !== "weightKg" && !Number.isInteger(n)) return "invalid";
  if (field === "weightKg" && Math.round(n * 100) !== n * 100) return "invalid";
  return n;
}

/** The writes the drafts make: ONE per cell whose typed value differs from what next week holds (a draft equal to the
 *  stored value writes nothing); `invalid` names every cell that cannot be read, and then nothing is written. */
export function cellWrites(cells: readonly NextCell[], drafts: Readonly<Record<string, string>>): { writes: SetFieldWrite[]; invalid: string[] } {
  const writes: SetFieldWrite[] = [];
  const invalid: string[] = [];
  for (const c of cells) {
    if (c.frozen) continue;
    const d = drafts[c.setId];
    if (d === undefined) continue;
    const v = parseCell(c.field, d, cellUnitFor(c));
    if (v === "invalid") { invalid.push(c.setId); continue; }
    if (v !== c.value) writes.push({ column: COLUMN[c.field], value: v, ids: [c.setId] });
  }
  return invalid.length > 0 ? { writes: [], invalid } : { writes, invalid };
}

/** Next week's tree as it WILL read once the writes land — for the words, so they describe what is being sent. */
export function draftedTree(tree: AssignedWorkoutTree, writes: readonly SetFieldWrite[]): AssignedWorkoutTree {
  const byId = new Map<string, SetFieldWrite>();
  for (const w of writes) for (const id of w.ids) byId.set(id, w);
  return {
    ...tree,
    coach_workout_sections: (tree.coach_workout_sections ?? []).map((s) => ({
      ...s,
      coach_workout_exercises: (s.coach_workout_exercises ?? []).map((e) => ({
        ...e,
        coach_workout_sets: (e.coach_workout_sets ?? []).map((x) => {
          const w = byId.get(x.id);
          return w === undefined ? x : { ...x, [w.column]: w.value };
        }),
      })),
    })),
  };
}

/* ── NEXT WEEK THROUGH THE BUILDER (his idea, 2026-10-07 ~10:20) ─────────────────────────────────────────────────
 * *"an edit workout thing or pencil for the whole thing … which will bring the workout edit window"*: ONE `Edit next week`
 * at the review's top opens the WORKOUT BUILDER on NEXT WEEK'S COPY (`#/review/<session>/next-week/<copy>`). The builder is
 * its OWN save, outside the review's one Send; the Next-week cells stay the review's draft. So the builder OPENS holding the
 * drafted numbers (`carryIntoWorkout` — the cells' own writes, on their set ids), and its SAVE (which replaces the copy's
 * rows, every set a new id) empties the drafts and the page reads again. A discarded builder writes nothing. The phone's
 * twin is `ReviewNextWeekEdit` (the same label, gate `review-next-week-through-the-builder`). */
export const EDIT_NEXT_WEEK_LABEL = "Edit next week";

type CarryRow = {
  coach_workout_sections?: ReadonlyArray<{
    coach_workout_exercises?: ReadonlyArray<{ coach_workout_sets?: ReadonlyArray<{ id: string }> | null }> | null;
  }> | null;
};
/** The builder's read of next week's copy with the review's drafted cells written onto THEIR sets (by id); a write whose
 *  set is not in the row is left out. Nothing else changes. */
export function carryIntoWorkout<T extends CarryRow>(row: T, writes: readonly SetFieldWrite[]): T {
  if (writes.length === 0) return row;
  const byId = new Map<string, SetFieldWrite>();
  for (const w of writes) for (const id of w.ids) byId.set(id, w);
  return {
    ...row,
    coach_workout_sections: (row.coach_workout_sections ?? []).map((s) => ({
      ...s,
      coach_workout_exercises: (s.coach_workout_exercises ?? []).map((e) => ({
        ...e,
        coach_workout_sets: (e.coach_workout_sets ?? []).map((x) => {
          const w = byId.get(x.id);
          return w === undefined ? x : { ...x, [w.column]: w.value };
        }),
      })),
    })),
  } as T;
}

/* ── the words under the button ─────────────────────────────────────────────────────────────────── */

/** `held (set 4 short)` · `held (sets 2 and 4 short)` · `held (sets 1, 2 and 4 short)`. */
export function heldWords(labels: readonly string[]): string {
  if (labels.length === 0) return "held";
  if (labels.length === 1) return `held (set ${labels[0] ?? ""} short)`;
  const head = labels.slice(0, -1).join(", ");
  return `held (sets ${head} and ${labels[labels.length - 1] ?? ""} short)`;
}

export type NextWeekWords = {
  /** Moved exercises, drawn by `MoveParts` — the overlay's lines. */
  readonly moved: readonly (MovedExercise & { readonly name: string })[];
  /** Exercises next week repeats although a set fell short: `Weighted Pull-Up · held (set 4 short)`. */
  readonly held: readonly { readonly name: string; readonly text: string }[];
  /** W136 B2 S3r3 A3 — a run / rounds row whose next week differs, said whole in §5's words (`phoneDose`, the dose line):
   *  `Tempo Run · 5 km in 25:00 → 6 km in 30:00`. Never through the overlay, which says a run's time as a hold
   *  (`1500 → 1800 s`) and cannot see a distance or rounds at all (MEASURED, `s3r3-a3-probe-before.log`). */
  readonly frozen: readonly { readonly key: string; readonly name: string; readonly from: string; readonly to: string }[];
  readonly unchanged: number;
  /** The chip: moved exercises (the overlay's own count shape) and changed run / rounds rows. */
  readonly changes: number;
};

/** A tree without the exercises whose (library exercise, role) is in `keys`. */
function withoutKeys(tree: AssignedWorkoutTree, keys: ReadonlySet<string>): AssignedWorkoutTree {
  return {
    ...tree,
    coach_workout_sections: (tree.coach_workout_sections ?? []).map((s) => ({
      ...s,
      coach_workout_exercises: (s.coach_workout_exercises ?? []).filter((e) => !keys.has(unitKey({ exerciseId: e.exercise_id ?? e.id, role: s.section_type }))),
    })),
  };
}

/** A frozen row's whole prescription in the dose line's words, its time typed (`5 km in 25:00`, `3 × 400 m`, `8 rounds`). */
function frozenRowWords(e: TreeExercise, km: boolean): string {
  return phoneDose(byPosition(e.coach_workout_sets).map((x) => ({
    reps: x.reps, time_seconds: x.time_seconds, weight_kg: num(x.weight_kg), set_type: x.set_type, rounds: x.rounds, distance_m: num(x.distance_m ?? null),
  })), km ? ["distance_km"] : [], e.hold_until_lost === true);
}

/** The list under the button, from the drafted next week against this week (the overlay's reader), with the short sets
 *  the sheet found by library exercise id. */
export function nextWeekWords(
  thisTree: AssignedWorkoutTree, draftedNext: AssignedWorkoutTree,
  names: Readonly<Record<string, string>>, shortByExercise: ReadonlyMap<string, readonly string[]>, kmKeys: ReadonlySet<string>,
): NextWeekWords {
  // A row frozen on either side (`isFrozen`) is said whole, paired as the cells pair it — the overlay never sees it.
  const keyed = (t: AssignedWorkoutTree) => treeExercises(t).map(({ exercise, role }) => ({ exercise, key: unitKey({ exerciseId: exercise.exercise_id ?? exercise.id, role }) }));
  const thisRows = keyed(thisTree), nextRows = keyed(draftedNext);
  const frozenKeys = new Set([...thisRows, ...nextRows].filter((r) => isFrozen(r.exercise)).map((r) => r.key));
  // W136 B2 S3r4 W2 — each item's own `key` (its unit key + its index there): the same run warming up and cooling down is
  // two units with ONE name, so the name was two items under one React key.
  const frozen: { key: string; name: string; from: string; to: string }[] = [];
  const frozenMoved = new Set<string>();
  let frozenSame = 0;
  for (const key of frozenKeys) {
    const mine = thisRows.filter((r) => r.key === key), theirs = nextRows.filter((r) => r.key === key);
    for (let i = 0; i < Math.min(mine.length, theirs.length); i += 1) {
      const a = mine[i]!.exercise, b = theirs[i]!.exercise;
      const km = b.exercise_id !== null && kmKeys.has(b.exercise_id);
      const from = frozenRowWords(a, km), to = frozenRowWords(b, km);
      if (from === to) frozenSame += 1;
      else { frozen.push({ key: `${key}#${String(i)}`, name: names[b.exercise_id ?? ""] ?? "Exercise", from, to }); frozenMoved.add(b.exercise_id ?? b.id); }
    }
  }
  const g = groupMoves(compareWeeks(flattenWeek(withoutKeys(thisTree, frozenKeys)), flattenWeek(withoutKeys(draftedNext, frozenKeys))));
  const movedIds = new Set([...g.moved.map((m) => m.exerciseId), ...g.added, ...g.dropped, ...frozenMoved]);
  const held: { name: string; text: string }[] = [];
  for (const [id, labels] of shortByExercise) {
    if (movedIds.has(id) || labels.length === 0) continue;
    held.push({ name: names[id] ?? "Exercise", text: heldWords(labels) });
  }
  return {
    moved: g.moved.map((m) => ({ ...m, name: names[m.exerciseId] ?? "Exercise" })),
    held,
    frozen,
    unchanged: Math.max(0, g.unchanged - held.length) + frozenSame,
    changes: g.moved.length + frozen.length,
  };
}

/* ── the rows ───────────────────────────────────────────────────────────────────────────────────── */

export type CommentDraft = {
  readonly exerciseRowId: string; readonly coachExerciseId: string; readonly exercise: string; readonly text: string;
  /** Step 5: a voice or video take sits in this exercise's containers — a comment with no words but a take IS a comment. */
  readonly hasMedia?: boolean;
};

export type MessageInsert = {
  readonly coach_id: string; readonly client_id: string; readonly sender_id: string; readonly body: string;
  readonly kind: "review" | "comment"; readonly session_id: string;
  readonly exercise_row_id: string | null; readonly coach_exercise_id: string | null;
  /** Step 5 — present only when `messageRows` is given `media`: the row's own id (the object paths are named by it) and
   *  the voice/video pairs (`reviewMedia.mediaFields`). */
  readonly id?: string;
} & MediaFields;

/** The comments that will be SENT: a draft with words in it, or with a voice/video take (step 5). An emptied draft with no
 *  take is no comment. */
export function commentsToSend(drafts: readonly CommentDraft[]): CommentDraft[] {
  return drafts.filter((d) => d.text.trim() !== "" || d.hasMedia === true);
}

/**
 * THE ROWS `Send review` INSERTS, in ONE statement: the review (always — it is the mark the queue reads) and one row per
 * comment with words. Every field the screen COLLECTED is here and nothing else: the review's text → the review's body;
 * each comment's text, row and library exercise → that comment's body and ids. `reviewWrite.test.ts` counts both sides.
 */
export function messageRows(p: {
  readonly coachId: string; readonly clientId: string; readonly sessionId: string;
  readonly title: string; readonly text: string; readonly comments: readonly CommentDraft[];
  /** Step 5: the recorder's slots (`overall` for the review, the exercise row's id for a comment) and an id minter. With it
   *  every row carries its own `id` and its takes' path pairs; without it the rows are exactly step 4's. */
  readonly media?: { readonly slots: Readonly<Record<SlotKey, Slot>>; readonly newId: () => string };
}): MessageInsert[] {
  const base = { coach_id: p.coachId, client_id: p.clientId, sender_id: p.coachId, session_id: p.sessionId };
  const rows: MessageInsert[] = [
    { ...base, kind: "review", body: reviewBody(p.title, p.text), exercise_row_id: null, coach_exercise_id: null },
    ...commentsToSend(p.comments).map((c) => ({
      ...base, kind: "comment" as const, body: commentBody(c.exercise, c.text), exercise_row_id: c.exerciseRowId, coach_exercise_id: c.coachExerciseId,
    })),
  ];
  const m = p.media;
  if (m === undefined) return rows;
  return rows.map((r) => {
    const id = m.newId();
    return { ...r, id, ...mediaFields(p.coachId, id, m.slots[slotOfRow(r)]) };
  });
}

/** The recorder slot a row's takes come from: the review's is `overall`, a comment's its exercise row. */
export function slotOfRow(r: Pick<MessageInsert, "kind" | "exercise_row_id">): SlotKey {
  return r.kind === "review" ? OVERALL : (r.exercise_row_id ?? OVERALL);
}

/** `Send review` is refused only while a cell cannot be read; a review with no words at all is still a review. */
export function sendBlocker(invalidCells: number): string | null {
  return invalidCells === 0 ? null : invalidCells === 1 ? "One of next week's numbers is not a number." : `${String(invalidCells)} of next week's numbers are not numbers.`;
}
